import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { categoryUtils } from 'bank-common';
import { Autosuggest, space } from 'folio-common-components';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { isProbablyMobile } from '../../../../utils/device';
import { getLedgerIcon } from '../../../icon';
import { CategoryListOption } from '../CategoryList';
import type { Subcategory } from '../types';

const { compareSubcategories, filterSubcategories } = categoryUtils;

type CategoryAutosuggestProps = {
  subcategories: Subcategory[];
  onSubcategorySelected: (guess: Subcategory) => void;
  className?: string;
  shouldFocus: boolean;
};

const StyledCategoryListOption = styled(CategoryListOption)`
  ${space([8], 'padding-horizontal')};
`;

const MemoizedCategoryListOption = React.memo(StyledCategoryListOption);

export const CategoryAutosuggest: React.FC<CategoryAutosuggestProps> = ({
  subcategories,
  onSubcategorySelected,
  className,
  shouldFocus,
}) => {
  const [query, setQuery] = React.useState('');
  const lowerCaseQuery = query.toLowerCase();

  const suggestions = filterSubcategories(subcategories, lowerCaseQuery).sort(
    (left, right) => compareSubcategories(left, right, lowerCaseQuery),
  );

  return (
    <div className={className}>
      <Autosuggest<Subcategory>
        theme={{
          suggestion: {},
          suggestionHighlighted: {
            color: colors.blue,
          },
          suggestionsList: {
            maxHeight: 300,
            padding: 0,
            margin: 0,
            listStyle: 'none',
          },
        }}
        highlightFirstSuggestion={true}
        inputProps={{
          placeholder: 'Skriv inn kategori…',
          value: query,
          autoFocus: shouldFocus && !isProbablyMobile,
          onChange: (_, e) => {
            if (e.method !== 'type') {
              return;
            }

            setQuery(e.newValue);
          },
          style: { width: '100%' },
        }}
        css={css`
          max-height: 100%;
        `}
        suggestions={suggestions}
        getSuggestionValue={() => ''}
        renderSuggestion={(category, { isHighlighted }) => (
          <MemoizedCategoryListOption
            label={category.title}
            description={category.subtitle}
            icon={getLedgerIcon(category.icon)}
            isHighlighted={isHighlighted}
          />
        )}
        onSuggestionsFetchRequested={() => undefined}
        onSuggestionsClearRequested={() => undefined}
        onSuggestionSelected={(_, { suggestion: subcategory }) => {
          onSubcategorySelected(subcategory);
        }}
      />
    </div>
  );
};
